import React from 'react';

/**
 * Minimal, dependency-free Markdown renderer for assistant replies.
 * Supports the subset the model emits: paragraphs, bullet and numbered lists,
 * and inline bold, italics, and code. Builds React elements (no
 * dangerouslySetInnerHTML), so text is escaped by React and safe to render.
 */

const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_)/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let i = 0;
  let match: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  while ((match = INLINE.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(
        <code key={key} className="rounded bg-black/10 px-1 py-0.5 text-[0.85em] dark:bg-white/15">
          {token.slice(1, -1)}
        </code>
      );
    } else {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    lastIndex = INLINE.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

interface Block {
  type: 'p' | 'ul' | 'ol';
  lines: string[];
}

function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;
  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };
  for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (bullet) {
      if (!current || current.type !== 'ul') {
        flush();
        current = { type: 'ul', lines: [] };
      }
      current.lines.push(bullet[1]);
    } else if (numbered) {
      if (!current || current.type !== 'ol') {
        flush();
        current = { type: 'ol', lines: [] };
      }
      current.lines.push(numbered[1]);
    } else if (line.trim() === '') {
      flush();
    } else {
      if (!current || current.type !== 'p') {
        flush();
        current = { type: 'p', lines: [] };
      }
      current.lines.push(line);
    }
  }
  flush();
  return blocks;
}

const MarkdownMessage: React.FC<{ text: string }> = ({ text }) => {
  const blocks = parseBlocks(text);
  return (
    <div className="space-y-2">
      {blocks.map((block, bi) => {
        if (block.type === 'ul') {
          return (
            <ul key={bi} className="list-disc space-y-0.5 pl-4">
              {block.lines.map((line, li) => (
                <li key={li}>{renderInline(line, `${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === 'ol') {
          return (
            <ol key={bi} className="list-decimal space-y-0.5 pl-4">
              {block.lines.map((line, li) => (
                <li key={li}>{renderInline(line, `${bi}-${li}`)}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={bi}>
            {block.lines.map((line, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(line, `${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default MarkdownMessage;
